import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  VoiceTrunkAddForm,
  VoiceTrunkAddInitialValues,
  VoiceTrunkComponents,
  VoiceTrunkAdditionalParams,
  VoiceTrunkFormOptions,
  onVoiceTrunkFormSaved,
  IVoiceTrunkFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateVoiceTrunk, PermissionEnum, } from 'atlas-shared';
import { createVoiceTrunk, fetchVoiceTrunk, getVoiceTrunkCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateVoiceTrunk> {}

const VoiceTrunkFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateVoiceTrunk>>();

  const param_props: IVoiceTrunkFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout = useMemo(() => VoiceTrunkAddForm(values, param_props, t), [values, organizations, t, abilities, ]);
  const options: IFormOptions = useMemo(() => VoiceTrunkFormOptions(values || VoiceTrunkAddInitialValues, param_props, t), [values, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateVoiceTrunk, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateVoiceTrunk) => save(() => createVoiceTrunk(values).then(
    res => onSaved ? onSaved(res, navigate) : onVoiceTrunkFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchVoiceTrunk(+id)
        .then(({
          organization_id,
          type,
          settings,
          title,
          description,
        }) => {
          setCloned({
            organization_id,
            type,
            settings,
            title,
            description,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.VoiceTrunk, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={VoiceTrunkComponents}
        additionalParams={VoiceTrunkAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...VoiceTrunkAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('VOICE_TRUNK')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const VoiceTrunkFormAddView = React.memo(withAdd(VoiceTrunkFormAdd, getVoiceTrunkCreateSchema, VoiceTrunkAddInitialValues));
